import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Breadcrumbs,
  Link,
  IconButton,
} from '@mui/material';
import { ArrowBack, Close } from '@mui/icons-material';
import { useTranslate, useDataProvider, useNotify, fetchUtils } from 'react-admin';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface WizardStep {
  id: string;
  title: string;
  component: React.ComponentType<any>;
  completed?: boolean;
}

interface DriverProfileWizardFullscreenProps {
  steps: WizardStep[];
  onComplete?: () => void;
  initialStep?: number;
  driverId?: number;
  userId?: number;
}

export const DriverProfileWizardFullscreen: React.FC<DriverProfileWizardFullscreenProps> = ({
  steps,
  onComplete,
  initialStep = 0,
  driverId,
  userId
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get step from URL or use initial
  const stepFromUrl = searchParams.get('step');
  const initialStepIndex = stepFromUrl ? steps.findIndex(s => s.id === stepFromUrl) : initialStep;

  const [currentStep, setCurrentStep] = useState(initialStepIndex >= 0 ? initialStepIndex : 0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [stepData, setStepData] = useState<Record<string, any>>({});
  const [driver, setDriver] = useState<any>(null);
  const [isClosing, setIsClosing] = useState(false);

  const translate = useTranslate();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const progress = ((completedSteps.size) / steps.length) * 100;

  // Update URL when step changes
  useEffect(() => {
    const currentStepId = steps[currentStep]?.id;
    if (currentStepId) {
      const newParams: Record<string, string> = { step: currentStepId };
      if (driverId) {
        newParams.driverId = driverId.toString();
      }
      setSearchParams(newParams);
    }
  }, [currentStep, steps, setSearchParams, driverId]);

  // Load existing driver data if editing
  useEffect(() => {
    if (driverId) {
      dataProvider.getOne('drivers', { id: driverId })
        .then(({ data }) => {
          setDriver(data);
          setStepData(prev => ({ ...prev, driver: data }));
        })
        .catch(error => {
          console.error('Error loading driver:', error);
        });
    }
  }, [driverId, dataProvider]);

  const saveStepData = async (stepId: string, data: any) => {
    try {
      if (stepId === 'driver' && data) {
        let result;
        if (driverId) {
          // PATCH update existing driver with only the changed fields
          const changedFields = Object.keys(data).reduce((acc, key) => {
            if (data[key] !== driver?.[key]) {
              acc[key] = data[key];
            }
            return acc;
          }, {} as any);

          if (Object.keys(changedFields).length > 0) {
            result = await dataProvider.update('drivers', {
              id: driverId,
              data: changedFields,
              previousData: driver
            });
            setDriver(result.data);
          } else {
            result = { data: driver };
          }
        } else {
          // Create new driver
          result = await dataProvider.create('drivers', { data });
          setDriver(result.data);
        }

        setStepData(prev => ({ ...prev, [stepId]: result.data }));
        notify('Driver profile saved successfully', { type: 'success' });
        return result.data;
      } else if (stepId === 'license') {
        // License step handles its own saving via the table Add/Edit functionality
        // No need to save again when clicking Next
        return null;
      } else if (stepId === 'education') {
        // Education step handles its own saving via the table Add/Edit functionality
        // No need to save again when clicking Next
        return null;
      } else if (stepId === 'employment') {
        // Employment step handles its own saving via the table Add/Edit functionality
        // No need to save again when clicking Next
        return null;
      } else if (stepId === 'certification') {
        // Certification step handles its own saving via the table Add/Edit functionality
        // No need to save again when clicking Next
        return null;
      } else if (stepId === 'safety' && data && driver?.id) {
        // Save safety & compliance data
        const result = await dataProvider.update('drivers', {
          id: driver.id,
          data: data
        });
        setStepData(prev => ({ ...prev, [stepId]: result.data }));
        notify('Safety & compliance information saved successfully', { type: 'success' });
        return result.data;
      } else if (stepId === 'documents' && data && driver?.id) {
        // Save documents data
        const result = await dataProvider.update('drivers', {
          id: driver.id,
          data: data
        });
        setStepData(prev => ({ ...prev, [stepId]: result.data }));
        notify('Documents information saved successfully', { type: 'success' });
        return result.data;
      }
    } catch (error: any) {
      notify('Error saving data: ' + (error.message || 'Unknown error'), { type: 'error' });
      throw error;
    }
  };

  const handleNext = async (data?: any) => {
    const currentStepId = steps[currentStep].id;
    
    try {
      // Save current step data if provided
      if (data) {
        const savedData = await saveStepData(currentStepId, data);
        if (currentStepId === 'driver' && savedData) {
          setDriver(savedData);
        }
      }

      // Mark step as completed
      setCompletedSteps(prev => new Set(prev).add(currentStep));
      
      // Move to next step or complete
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error saving step data:', error);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    // Prevent double-clicking and stop event propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent multiple close attempts
    if (isClosing) {
      return;
    }

    setIsClosing(true);

    try {
      // If onComplete callback is provided, use it (this is the primary exit mechanism)
      if (onComplete) {
        onComplete();
        return;
      }

      // Check if user is standalone (no tenants)
      const isStandalone = localStorage.getItem('isStandalone') === 'true';

      if (isStandalone) {
        // Standalone user - go back to standalone dashboard
        navigate('/standalone-dashboard');
      } else if (userId) {
        // Tenant user - navigate back to their driver profile page with snapshot tab
        navigate(`/users/${userId}/show/1?tab=snapshot`);
      } else {
        // Fallback to users list
        navigate('/users');
      }
    } catch (error) {
      console.error('Error in handleClose:', error);
      setIsClosing(false);
      // Fallback navigation
      navigate('/users');
    }
  };

  const CurrentStepComponent = steps[currentStep]?.component;

  // Breadcrumb navigation
  const breadcrumbSteps = [
    'Profile Builder',
    steps[currentStep]?.title || '',
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      position: 'relative'
    }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0',
        p: 2
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: 1200,
          mx: 'auto'
        }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb">
            {breadcrumbSteps.map((step, index) => (
              <Typography 
                key={index}
                color={index === breadcrumbSteps.length - 1 ? 'primary' : 'text.primary'}
                sx={{ fontWeight: index === breadcrumbSteps.length - 1 ? 'bold' : 'normal' }}
              >
                {step}
              </Typography>
            ))}
          </Breadcrumbs>

          {/* Close button */}
          <IconButton
            onClick={handleClose}
            disabled={isClosing}
            title="Close wizard"
          >
            <Close />
          </IconButton>
        </Box>

        {/* Progress bar */}
        <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Step {currentStep + 1} of {steps.length}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#1976d2'
              }
            }}
          />
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        maxWidth: 800,
        mx: 'auto',
        p: 4
      }}>
        <Paper sx={{ 
          p: 4,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {CurrentStepComponent && (
            <CurrentStepComponent
              onNext={handleNext}
              onBack={handleBack}
              onClose={handleClose}
              existingData={stepData[steps[currentStep].id]}
              driver={driver}
              isFirstStep={currentStep === 0}
              isLastStep={currentStep === steps.length - 1}
              userId={userId}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};